// Kid-facing teaching content library.
//
// Each `TOPIC` is a themed unit of real syllabus knowledge (e.g. "The 7
// Continents & 5 Oceans", "Times Tables 2, 5 & 10"). It carries 3
// age-tiered explanations (5-7 / 8-10 / 11-15) so the same theme scales
// with the reader. The `outcomes` array lists every syllabus outcome code
// this topic satisfies — used by `LearnTopics.jsx` to attach rich
// teaching content beneath each outcome, spanning UK, IB, Cambridge, UAE
// and Common Core curricula.
//
// Voice: kid-friendly, plain English, no jargon. Each tier delivers:
//   • `intro`   — 2-3 sentence explainer
//   • `facts`   — 4-6 concrete facts a child can recall / cite
//   • `funFact` — one "wow!" hook
//   • `tryThis` — { text, to } linking to an in-app activity
//
// Adding a new topic? Follow this template and remember to list every
// outcome code it teaches so the LearnTopics browser wires it up.

/**
 * @typedef {Object} TopicTier
 * @property {string} intro
 * @property {string[]} facts
 * @property {string} funFact
 * @property {{text:string, to:string}} tryThis
 */

/**
 * @typedef {Object} Topic
 * @property {string} id
 * @property {string} title
 * @property {string} emoji
 * @property {string} subject
 * @property {string[]} outcomes  Syllabus outcome codes covered by this topic
 * @property {{ "5-7":TopicTier, "8-10":TopicTier, "11-15":TopicTier }} tiers
 */

/** @type {Topic[]} */
export const TOPICS = [
  // ═════════════════════════ GEOGRAPHY ═════════════════════════
  {
    id: "continents-oceans",
    title: "The 7 Continents & 5 Oceans",
    emoji: "🌍",
    subject: "geography",
    outcomes: [
      "uk-ks1-geo-1","uk-ks2-geo-1","uk-ks3-geo-1","ib-pyp-geo-1",
      "cam-prim-geo-1","uae-moe-ss-1","cc-ss-1","eyfs-utw-1",
    ],
    tiers: {
      "5-7": {
        intro: "The world is made of huge pieces of land called continents, and enormous bodies of salty water called oceans. There are 7 continents and 5 oceans.",
        facts: [
          "The 7 continents are Africa, Antarctica, Asia, Australia (Oceania), Europe, North America and South America.",
          "The 5 oceans are Pacific, Atlantic, Indian, Southern and Arctic.",
          "Asia is the biggest continent — more than 4 billion people live there.",
          "Antarctica is covered in ice and has almost no people, only scientists and penguins!",
          "The Pacific is the biggest ocean and touches Asia, Australia and the Americas.",
        ],
        funFact: "You could fit every other continent inside the Pacific Ocean — and still have room left over!",
        tryThis: { text: "Spin the 3D Globe and tap each continent", to: "/globe" },
      },
      "8-10": {
        intro: "Continents are the world's largest landmasses; oceans are the connected bodies of salt water between them. Together they cover almost every part of Earth's surface.",
        facts: [
          "Continents by size: Asia > Africa > North America > South America > Antarctica > Europe > Oceania.",
          "Oceans by size: Pacific > Atlantic > Indian > Southern > Arctic.",
          "The Equator runs through Africa, South America and Asia — the hottest continents.",
          "About 71% of Earth is covered in water and only 29% is land.",
          "The Southern Ocean was officially named the 5th ocean in 2021.",
          "Europe and Asia sit on the same landmass (Eurasia) but are counted as two continents for cultural reasons.",
        ],
        funFact: "The Mariana Trench in the Pacific is deeper than Mount Everest is tall — nearly 11 kilometres straight down!",
        tryThis: { text: "Take the Continent Map Tour", to: "/continents" },
      },
      "11-15": {
        intro: "Earth's surface is split into continental plates that carry the seven continents and cradle five oceans. Their positions shape climate zones, biomes and human civilisation.",
        facts: [
          "Continental drift means the continents were once joined as Pangaea; the Atlantic is still widening ~2.5cm a year.",
          "The Pacific covers about 46% of Earth's water surface and is bigger than all land combined.",
          "Ocean currents (Gulf Stream, Kuroshio) move heat between the equator and poles and drive weather.",
          "Antarctica holds ~70% of the world's fresh water — most of it locked as ice.",
          "The Ring of Fire around the Pacific is home to ~75% of the world's active volcanoes.",
          "Eurasia is the largest single landmass; Australia is both a country and a continent.",
        ],
        funFact: "If Antarctica's ice fully melted, global sea levels would rise about 60 metres — enough to drown London, New York and Shanghai.",
        tryThis: { text: "Explore continents on the 3D Globe", to: "/globe" },
      },
    },
  },
  {
    id: "world-countries-capitals",
    title: "World Countries & Capitals",
    emoji: "🏙️",
    subject: "geography",
    outcomes: [
      "uk-ks1-geo-2","uk-ks2-geo-2","uk-ks3-geo-2","ib-pyp-geo-2",
      "cam-prim-geo-2","uae-moe-ss-2","cc-ss-2","cc-ss-3",
    ],
    tiers: {
      "5-7": {
        intro: "A country is an area of land with its own government and flag. The most important city in a country — where the leaders work — is called the capital.",
        facts: [
          "The United Nations recognises 193 member states plus 2 observer states.",
          "The capital of France is Paris. The capital of Japan is Tokyo. The capital of Egypt is Cairo.",
          "The UK is made of 4 countries: England, Scotland, Wales and Northern Ireland.",
          "Every country has its own flag with special colours and symbols.",
          "The USA has 50 states, each with its own capital, plus one big capital: Washington D.C.",
        ],
        funFact: "Vatican City is the smallest country — it's smaller than most zoos!",
        tryThis: { text: "Learn 3 countries today", to: "/learn" },
      },
      "8-10": {
        intro: "Countries are political units marked out by borders. Each has a capital city where the government sits, a flag, a language and often a currency.",
        facts: [
          "The 5 biggest countries by area: Russia, Canada, USA, China, Brazil.",
          "The 5 most populous countries: India, China, USA, Indonesia, Pakistan.",
          "Landlocked countries have no coast — e.g. Switzerland, Nepal, Bolivia.",
          "Island countries include Japan, the UK, New Zealand and Madagascar.",
          "Some countries have two or more capitals — South Africa has three: Pretoria, Cape Town, Bloemfontein.",
        ],
        funFact: "Canberra was built from scratch as Australia's capital because Sydney and Melbourne couldn't agree!",
        tryThis: { text: "Spell the country game", to: "/spell" },
      },
      "11-15": {
        intro: "Nations are sovereign states recognised by the international community. Their capitals often reflect history, geography or political compromise, and their borders result from treaties, conquest or colonial legacy.",
        facts: [
          "The United Nations recognises 193 member states plus 2 observer states (Vatican City & Palestine).",
          "Some countries have de-facto and de-jure capitals — Israel/Jerusalem is a well-known example.",
          "Post-colonial borders often follow lines drawn in Europe, not local ethnic groups (e.g. the Berlin Conference 1884).",
          "Micro-states (Monaco, San Marino, Liechtenstein) survive as cultural relics of medieval Europe.",
          "The G20 groups 19 of the world's largest economies plus the European Union.",
        ],
        funFact: "Naypyidaw became Myanmar's capital in 2005 — the entire city was built almost from scratch and is 10 times the area of Paris but has 20 times fewer people.",
        tryThis: { text: "Take the geography quiz", to: "/quiz" },
      },
    },
  },
  {
    id: "maps-globes",
    title: "Maps, Atlases & Globes",
    emoji: "🗺️",
    subject: "geography",
    outcomes: [
      "uk-ks1-geo-3","uk-ks2-geo-3","uk-ks3-geo-3","ib-pyp-geo-3",
      "cam-prim-geo-3","cc-ss-4",
    ],
    tiers: {
      "5-7": {
        intro: "A map is a picture from above that shows where places are. A globe is a round map — the same shape as Earth.",
        facts: [
          "Maps use colours: blue is water, green is grass and land, brown is mountains.",
          "North is usually at the top of a map.",
          "An atlas is a book of maps.",
          "A globe spins because Earth spins too!",
          "A key or legend tells you what the pictures on a map mean.",
        ],
        funFact: "Some maps have dragons in the sea — that's how old sailors said 'we don't know what's there yet'!",
        tryThis: { text: "Play with the 3D Globe", to: "/globe" },
      },
      "8-10": {
        intro: "Maps flatten a round Earth onto paper using a projection. Different projections show different things — some keep shapes right, others keep sizes right.",
        facts: [
          "The compass rose shows N, S, E, W directions.",
          "Latitude lines run east-west; longitude lines run north-south.",
          "The Equator is 0° latitude; the Prime Meridian is 0° longitude and runs through Greenwich, London.",
          "A scale bar tells you how much real distance a bit of map represents.",
          "The Mercator projection makes Greenland look as big as Africa — but Africa is 14× larger!",
        ],
        funFact: "GPS satellites orbit 20,000 km above Earth and can tell where you are to within a few metres.",
        tryThis: { text: "Try Geo Lines — grids & coordinates", to: "/geo-lines" },
      },
      "11-15": {
        intro: "Cartography is the science of representing three-dimensional space in two dimensions. Every projection distorts either shape, area, distance or direction — cartographers choose based on the map's purpose.",
        facts: [
          "Common projections: Mercator (navigation), Robinson (world maps), Peters (equal-area), Winkel Tripel (National Geographic).",
          "GPS uses trilateration from at least 4 satellites for accurate 3D position.",
          "GIS (Geographic Information Systems) layers data on maps — used in city planning, epidemiology and climate science.",
          "Topographic maps use contour lines to show elevation; closer lines = steeper terrain.",
          "The world's oldest known map is a 25,000 year-old mammoth tusk carving showing rivers in the Czech Republic.",
        ],
        funFact: "OpenStreetMap is built by volunteers — over 10 million editors have mapped roads, buildings and paths in every country.",
        tryThis: { text: "Explore world lines & grids", to: "/geo-lines" },
      },
    },
  },
  {
    id: "physical-geography",
    title: "Rivers, Mountains & Physical Features",
    emoji: "⛰️",
    subject: "geography",
    outcomes: [
      "uk-ks2-geo-4","uk-ks3-geo-4","ib-pyp-geo-4","cam-prim-geo-4",
      "uae-moe-ss-3","cc-ss-5",
    ],
    tiers: {
      "5-7": {
        intro: "Earth has amazing shapes on it — pointy mountains, long rivers, dry deserts and wet rainforests. These are called physical features.",
        facts: [
          "The tallest mountain is Mount Everest, in Asia.",
          "The longest river is the Nile in Africa (or the Amazon in South America — scientists still argue!).",
          "The biggest desert is the Sahara — bigger than the whole USA.",
          "The Amazon rainforest is home to millions of animals and plants.",
          "A volcano is a mountain that can burp hot rock called lava.",
        ],
        funFact: "Angel Falls in Venezuela is so tall (979m) that the water turns into mist before hitting the ground!",
        tryThis: { text: "Explore world wonders", to: "/explore/wonders" },
      },
      "8-10": {
        intro: "Physical geography studies Earth's natural shapes: mountains, rivers, deserts, forests, coasts and climates.",
        facts: [
          "Mountain ranges: the Himalayas (Asia), Andes (S. America), Rockies (N. America), Alps (Europe).",
          "Major rivers: Nile, Amazon, Yangtze, Mississippi, Danube.",
          "Deserts include the Sahara, Gobi, Kalahari and Antarctic Desert (cold deserts count too!).",
          "Biomes are big regions with a shared climate — tundra, taiga, rainforest, savanna, desert.",
          "Rivers carry water from mountains to oceans and shape the land as they flow.",
        ],
        funFact: "The Andes are still growing about 1 cm a year as tectonic plates keep pushing them up.",
        tryThis: { text: "Explore Nature Hub", to: "/explore/nature" },
      },
      "11-15": {
        intro: "Landforms result from tectonic, weathering and erosional processes over geological time. Water, ice, wind and living organisms all sculpt the surface.",
        facts: [
          "Plate tectonics: convergent, divergent and transform boundaries create mountains, ridges and faults.",
          "River processes: erosion, transport and deposition form meanders, oxbow lakes and deltas.",
          "Weathering (physical, chemical, biological) breaks rock; erosion moves it.",
          "The rock cycle links igneous, sedimentary and metamorphic rocks over millions of years.",
          "Global biomes correlate with latitude and altitude — tropical rainforest at equator, tundra near poles.",
        ],
        funFact: "The Grand Canyon exposes ~2 billion years of Earth's history — nearly half the age of the planet — in its rock layers.",
        tryThis: { text: "Explore Nature Hub", to: "/explore/nature" },
      },
    },
  },
  {
    id: "climate-weather",
    title: "Weather, Climate & Seasons",
    emoji: "🌦️",
    subject: "geography",
    outcomes: [
      "uk-ks1-sc-3","uk-ks2-geo-5","uk-ks3-geo-5","ib-pyp-geo-5",
      "cc-ss-6",
    ],
    tiers: {
      "5-7": {
        intro: "Weather is what the sky is doing today — sunny, rainy, snowy. Seasons are patterns of weather that come around every year.",
        facts: [
          "The 4 seasons are Spring, Summer, Autumn (Fall) and Winter.",
          "In Australia, seasons are flipped — Christmas is in summer!",
          "Water in clouds becomes rain when the droplets get too heavy.",
          "Snow forms when it's very cold high up in the clouds.",
          "The sun is closer to us in summer? No! The Earth tilts — that's what makes seasons.",
        ],
        funFact: "The wettest place on Earth is Mawsynram, India — it rains about 12 metres a year, higher than a giraffe stacked on a giraffe!",
        tryThis: { text: "Explore Nature", to: "/explore/nature" },
      },
      "8-10": {
        intro: "Weather is short-term (today, this week). Climate is the long-term pattern (typical weather over years).",
        facts: [
          "The water cycle: evaporation → condensation → precipitation → collection → repeat.",
          "The 3 main climate zones are polar (cold), temperate (mild) and tropical (hot).",
          "Hurricanes, typhoons and cyclones are the same storm — different names in different oceans.",
          "The Earth's axis is tilted 23.5° — that tilt causes seasons.",
          "Climate change is a slow warming of Earth caused by extra greenhouse gases.",
        ],
        funFact: "Antarctica once had rainforests! Fossils show trees grew there 90 million years ago when it was warmer.",
        tryThis: { text: "Ask the Globe about weather", to: "/ask" },
      },
      "11-15": {
        intro: "Climate is the statistical average of weather over 30+ years. Global climate is driven by solar radiation, atmospheric circulation, ocean currents and greenhouse gases.",
        facts: [
          "Köppen climate classification uses temperature and precipitation to define zones (Af, Aw, BWh, Cfb…).",
          "Hadley, Ferrel and Polar cells describe global air circulation.",
          "ENSO (El Niño / La Niña) shifts Pacific temperatures and affects weather globally.",
          "CO₂ has risen from 280 ppm (pre-industrial) to over 420 ppm today.",
          "Positive feedback loops (ice melt → less reflection → more warming) accelerate change.",
        ],
        funFact: "The IPCC 6th Assessment (2023) states human influence has warmed the atmosphere, ocean and land 'unequivocally' — a first for scientific consensus.",
        tryThis: { text: "Ask the Globe about climate", to: "/ask" },
      },
    },
  },

  // ═════════════════════════ MATHS ═════════════════════════
  {
    id: "counting-place-value",
    title: "Counting, Numbers & Place Value",
    emoji: "🔢",
    subject: "maths",
    outcomes: [
      "eyfs-ma-2","uk-ks1-ma-1","uk-ks2-ma-1","cam-prim-ma-1",
      "cc-ma-1","uae-moe-ma-1",
    ],
    tiers: {
      "5-7": {
        intro: "Numbers help us count things. When you write a number like 32, the '3' means 3 tens and the '2' means 2 ones. That's called place value.",
        facts: [
          "Count on: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10.",
          "Count in 2s: 2, 4, 6, 8, 10.",
          "Count in 5s: 5, 10, 15, 20, 25.",
          "Count in 10s: 10, 20, 30, 40, 50.",
          "In 45, the 4 is in the tens place and the 5 is in the ones place.",
        ],
        funFact: "The biggest number word most kids know is 'million' — but there are numbers so big they need googolplex digits to write!",
        tryThis: { text: "Try Maths Academy", to: "/maths-academy" },
      },
      "8-10": {
        intro: "Numbers can be small or huge. Place value lets us read any number: hundreds, thousands, millions… each column is 10× the one to its right.",
        facts: [
          "1 thousand = 1,000. 1 million = 1,000,000 (a thousand thousands).",
          "Round to the nearest 10: 46 → 50, 42 → 40.",
          "Negative numbers are below zero: −5 is 5 less than 0.",
          "A decimal like 3.7 means 3 whole ones and 7 tenths.",
          "Roman numerals: I, V, X, L, C, D, M = 1, 5, 10, 50, 100, 500, 1000.",
        ],
        funFact: "A googol is 1 followed by 100 zeros — more than the number of atoms in the observable universe!",
        tryThis: { text: "Play Squishy Hop math", to: "/squishy" },
      },
      "11-15": {
        intro: "Our number system is base-10 (decimal) with positional place value. Other bases exist — computers use base-2 (binary), programmers sometimes use base-16 (hex).",
        facts: [
          "Powers of 10: 10¹=10, 10²=100, 10³=1000, 10⁻¹=0.1.",
          "Scientific notation: 6.02 × 10²³ is Avogadro's number.",
          "Rational numbers can be written as a fraction; irrational cannot (π, √2).",
          "Binary: 1011 = 8+0+2+1 = 11 in decimal.",
          "Prime numbers have only 2 factors — 1 and themselves. Every whole number > 1 has a unique prime factorisation.",
        ],
        funFact: "The Mayans invented zero independently around 350 AD — centuries before Europeans widely used it.",
        tryThis: { text: "Take on Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "add-subtract",
    title: "Addition & Subtraction",
    emoji: "➕",
    subject: "maths",
    outcomes: [
      "uk-ks1-ma-2","uk-ks2-ma-2","cam-prim-ma-2","cc-ma-2","uae-moe-ma-2","eyfs-ma-1",
    ],
    tiers: {
      "5-7": {
        intro: "Adding (+) puts groups together. Subtracting (−) takes some away.",
        facts: [
          "3 + 2 = 5. That's a number bond to 5.",
          "Number bonds to 10: 1+9, 2+8, 3+7, 4+6, 5+5.",
          "Subtraction is the opposite of addition: 5 − 2 = 3.",
          "You can add in any order: 4 + 3 is the same as 3 + 4.",
          "Doubles help: double 6 is 12.",
        ],
        funFact: "Some birds can count! Crows have been shown to tell 5 people apart from 4 people.",
        tryThis: { text: "Play Math Snap", to: "/snap" },
      },
      "8-10": {
        intro: "Column addition and subtraction let you handle big numbers. Line the digits up by place value and work right to left.",
        facts: [
          "Carry when a column adds to more than 9 (e.g. 27 + 15 → carry the 1 into tens).",
          "Borrow (regroup) when the top digit is smaller: 42 − 17 → borrow 1 from tens.",
          "Inverse operation: if 25 + 8 = 33, then 33 − 8 = 25.",
          "Estimate first: 198 + 405 ≈ 200 + 400 = 600.",
          "Mental strategy: near-doubles (7+8 = double 7 + 1 = 15).",
        ],
        funFact: "The '=' equals sign was invented in 1557 by Welsh maths teacher Robert Recorde — because he was 'tired of writing is equal to'.",
        tryThis: { text: "Play Geo Math", to: "/math" },
      },
      "11-15": {
        intro: "Addition and subtraction extend to integers, decimals, fractions and algebraic expressions. The same rules — commutativity, associativity, additive inverse — apply.",
        facts: [
          "Commutative: a + b = b + a.",
          "Associative: (a + b) + c = a + (b + c).",
          "Additive inverse: a + (−a) = 0.",
          "Adding fractions needs a common denominator: 1/2 + 1/3 = 3/6 + 2/6 = 5/6.",
          "Algebra: 3x + 5x = 8x (combine like terms).",
        ],
        funFact: "The Roman abacus is over 2000 years old and could do complex additions faster than early paper methods.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "multiply-divide",
    title: "Multiplication & Division",
    emoji: "✖️",
    subject: "maths",
    outcomes: [
      "uk-ks1-ma-3","uk-ks2-ma-3","cam-prim-ma-3","cc-ma-3","uae-moe-ma-3",
    ],
    tiers: {
      "5-7": {
        intro: "Multiplying is fast adding. 3 × 4 means 4 lots of 3, or 4 + 4 + 4. Dividing shares things equally.",
        facts: [
          "2 times table: 2, 4, 6, 8, 10, 12, 14, 16, 18, 20.",
          "5 times table: 5, 10, 15, 20, 25, 30, 35, 40, 45, 50.",
          "10 times table: just add a zero — 10, 20, 30, 40, 50.",
          "12 ÷ 4 = 3 (share 12 into 4 equal groups).",
          "0 × anything = 0.",
        ],
        funFact: "The '×' symbol was invented by English mathematician William Oughtred in 1631.",
        tryThis: { text: "Play Squishy Hop", to: "/squishy" },
      },
      "8-10": {
        intro: "Times tables (up to 12×12) unlock the whole of arithmetic — long multiplication, division, fractions and percentages all lean on them.",
        facts: [
          "Long multiplication: 24 × 6 → 20 × 6 + 4 × 6 = 120 + 24 = 144.",
          "Bus-stop division: 84 ÷ 4 = 21.",
          "Multiplying by 10 shifts every digit one place to the left.",
          "Factor pairs of 24: 1×24, 2×12, 3×8, 4×6.",
          "Remainders: 17 ÷ 5 = 3 remainder 2.",
        ],
        funFact: "The 9-times table has a trick — the digits of every answer add to 9 (9, 18, 27, 36…).",
        tryThis: { text: "Practise on Maths Academy", to: "/maths-academy" },
      },
      "11-15": {
        intro: "Multiplication scales quantities; division is the inverse. Both extend to negative numbers, fractions, decimals, algebraic expressions and beyond.",
        facts: [
          "Distributive law: a(b + c) = ab + ac.",
          "Multiplying negatives: −3 × −4 = +12.",
          "Index laws: aᵐ × aⁿ = aᵐ⁺ⁿ; aᵐ ÷ aⁿ = aᵐ⁻ⁿ.",
          "Standard form: 3.6 × 10⁴ = 36,000.",
          "Division as a fraction: 7 ÷ 4 = 7/4 = 1.75.",
        ],
        funFact: "Ancient Egyptians multiplied by doubling and adding — no times tables needed. It still works!",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "fractions",
    title: "Fractions, Decimals & Percentages",
    emoji: "½",
    subject: "maths",
    outcomes: [
      "uk-ks1-ma-4","uk-ks2-ma-4","cam-prim-ma-4","cc-ma-4","uae-moe-ma-4",
    ],
    tiers: {
      "5-7": {
        intro: "A fraction is a part of a whole. 1/2 means one out of two equal parts. Cut a pizza into 2 slices — one slice is 1/2.",
        facts: [
          "1/2 is half.",
          "1/4 is a quarter.",
          "1/3 is a third.",
          "Two halves make a whole: 1/2 + 1/2 = 1.",
          "4 quarters make a whole: 1/4 + 1/4 + 1/4 + 1/4 = 1.",
        ],
        funFact: "You use fractions every day — telling the time! Quarter past means 1/4 of the hour has gone.",
        tryThis: { text: "Try Geo Math", to: "/math" },
      },
      "8-10": {
        intro: "Fractions, decimals and percentages are three ways of writing the same idea — a part of a whole.",
        facts: [
          "1/2 = 0.5 = 50%.",
          "1/4 = 0.25 = 25%.",
          "3/4 = 0.75 = 75%.",
          "Equivalent fractions: 2/4 = 1/2, 6/8 = 3/4.",
          "To find 10% of a number, divide by 10.",
        ],
        funFact: "The word 'percent' comes from Latin 'per centum' meaning 'per hundred'.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
      "11-15": {
        intro: "Fractions are rational numbers of the form a/b (b ≠ 0). Decimals and percentages are equivalent representations that make calculation and comparison easier.",
        facts: [
          "Adding fractions: a/b + c/d = (ad + bc) / bd.",
          "Multiplying: a/b × c/d = ac/bd.",
          "Dividing: a/b ÷ c/d = a/b × d/c.",
          "Recurring decimals: 1/3 = 0.333…, 1/7 = 0.142857 (repeating).",
          "Percentage change = (new − old) / old × 100.",
        ],
        funFact: "The Babylonians used fractions with base 60 — that's why we still have 60 seconds in a minute and 360° in a circle.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "shapes-geometry",
    title: "Shapes & Geometry",
    emoji: "📐",
    subject: "maths",
    outcomes: [
      "uk-ks1-ma-7","uk-ks2-ma-6","cam-prim-ma-5","cc-ma-5","uae-moe-ma-5",
    ],
    tiers: {
      "5-7": {
        intro: "Shapes are all around us. Flat shapes (2D) live on paper; solid shapes (3D) take up space.",
        facts: [
          "2D shapes: circle, triangle, square, rectangle, pentagon (5 sides), hexagon (6 sides).",
          "3D shapes: cube, sphere, cylinder, cone, pyramid.",
          "A square has 4 equal sides and 4 right angles.",
          "A triangle has 3 sides and 3 corners.",
          "A cube has 6 square faces, 8 corners and 12 edges.",
        ],
        funFact: "Bees build their honeycomb from hexagons because hexagons use the least wax to hold the most honey!",
        tryThis: { text: "Play a shape puzzle", to: "/puzzles" },
      },
      "8-10": {
        intro: "Geometry studies shape, size, angle and position. Every polygon and solid follows rules — knowing them lets you build anything.",
        facts: [
          "Angles: acute (<90°), right (90°), obtuse (>90°), reflex (>180°).",
          "Angles in a triangle add to 180°; in a quadrilateral, 360°.",
          "Regular polygons have equal sides and equal angles.",
          "Perimeter is the distance around; area is the space inside.",
          "Area of a rectangle = length × width.",
        ],
        funFact: "The Pentagon (US HQ) is one of the largest buildings in the world — and yes, it has 5 sides!",
        tryThis: { text: "Try Geo Lines", to: "/geo-lines" },
      },
      "11-15": {
        intro: "Euclidean geometry starts from postulates about points, lines and planes; from these, thousands of theorems follow. Coordinate geometry lets us do it with algebra.",
        facts: [
          "Pythagoras' theorem: a² + b² = c² (right-angled triangles).",
          "Circle: circumference = 2πr, area = πr².",
          "Volume: cuboid = l×w×h; cylinder = πr²h; sphere = 4/3 πr³.",
          "Transformations: translation, rotation, reflection, enlargement.",
          "The interior angle of a regular polygon with n sides = (n−2) × 180 / n.",
        ],
        funFact: "Euclid's 'Elements' (300 BC) is the most-published maths book in history — over 1000 editions.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "measurement-time-money",
    title: "Measurement, Time & Money",
    emoji: "📏",
    subject: "maths",
    outcomes: [
      "uk-ks1-ma-5","uk-ks1-ma-6","uk-ks2-ma-5","cam-prim-ma-6",
      "cc-ma-6","uae-moe-ma-6",
    ],
    tiers: {
      "5-7": {
        intro: "We measure length (how long), mass (how heavy), volume (how much fits), time (when) and money (how much it costs).",
        facts: [
          "1 metre (m) is about a big step. 100 centimetres = 1 metre.",
          "1 kilogram (kg) is about the weight of a bag of sugar.",
          "1 litre (L) is a big bottle of milk.",
          "24 hours in a day, 7 days in a week, 12 months in a year.",
          "£1 = 100p. $1 = 100¢. €1 = 100 cents.",
        ],
        funFact: "The clock is called 'clock' because it comes from the old word for 'bell' — early clocks just rang the hour!",
        tryThis: { text: "Try Geo Math", to: "/math" },
      },
      "8-10": {
        intro: "The metric system uses base-10 for length (m), mass (g), volume (L) and time (s). Money uses base-10 in most countries too.",
        facts: [
          "1 km = 1000 m. 1 m = 100 cm. 1 cm = 10 mm.",
          "1 kg = 1000 g. 1 tonne = 1000 kg.",
          "1 L = 1000 ml. 1 m³ = 1000 L.",
          "Reading time: 15:45 is 3:45 pm; 07:30 is half past 7.",
          "Currency conversion uses exchange rates: £1 might buy $1.25 today.",
        ],
        funFact: "The metric system was born in France in 1795 — before that, every town had its own units, which made trade a nightmare!",
        tryThis: { text: "Try Geo Math", to: "/math" },
      },
      "11-15": {
        intro: "Measurement uses SI base units (metre, kilogram, second, ampere, kelvin, mole, candela) and derived units. Unit conversion, significant figures and error handling are essential skills.",
        facts: [
          "Speed = distance ÷ time. Density = mass ÷ volume.",
          "Compound units: m/s (speed), m/s² (acceleration), kg/m³ (density).",
          "Time zones: UTC±hours from Greenwich; there are 24 standard zones.",
          "Percentage error = |measured − true| / true × 100.",
          "Interest: simple = P × r × t; compound = P × (1 + r)ⁿ.",
        ],
        funFact: "In 2019 the kilogram was redefined against Planck's constant — the last physical artefact standard ever!",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },

  // ═════════════════════════ SCIENCE ═════════════════════════
  {
    id: "living-things",
    title: "Animals, Plants & Living Things",
    emoji: "🐾",
    subject: "science",
    outcomes: [
      "eyfs-utw-2","uk-ks1-sc-1","uk-ks1-sc-2","uk-ks1-sc-5",
      "uk-ks2-sc-1","cam-prim-sc-1","cc-sc-1",
    ],
    tiers: {
      "5-7": {
        intro: "Every living thing needs food, water and air. Animals and plants live in special places called habitats.",
        facts: [
          "Mammals have fur and feed babies with milk.",
          "Birds have feathers and lay eggs.",
          "Fish live in water and breathe through gills.",
          "Plants have roots, a stem, leaves and (sometimes) flowers.",
          "A habitat is where an animal lives — a pond, forest, desert or ocean.",
        ],
        funFact: "The largest animal ever is alive today: the blue whale, longer than 3 buses!",
        tryThis: { text: "Explore Animals", to: "/explore/animals" },
      },
      "8-10": {
        intro: "All life is grouped into kingdoms: animals, plants, fungi, bacteria and protists. Inside each kingdom, life is sorted by shared traits.",
        facts: [
          "Vertebrates have a backbone (mammals, birds, fish, reptiles, amphibians).",
          "Invertebrates don't (insects, spiders, worms, jellyfish).",
          "Plants make food from sunlight in a process called photosynthesis.",
          "A food chain: sun → grass → rabbit → fox.",
          "Ecosystems break down when one link is removed.",
        ],
        funFact: "There are more insects on Earth than every other animal combined — 10 quintillion of them!",
        tryThis: { text: "Explore Animals", to: "/explore/animals" },
      },
      "11-15": {
        intro: "Biological classification (taxonomy) organises life from domain to species. Ecosystems, adaptation and natural selection explain how species survive, thrive or disappear.",
        facts: [
          "Linnaean hierarchy: Domain, Kingdom, Phylum, Class, Order, Family, Genus, Species.",
          "Photosynthesis: 6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂.",
          "Respiration is the reverse process, releasing energy.",
          "Natural selection (Darwin, 1859): variation + inheritance + selection pressure = evolution.",
          "Biodiversity supports ecosystem resilience; ~1 million species face extinction today (IPBES 2019).",
        ],
        funFact: "Tardigrades ('water bears') can survive being boiled, frozen, dehydrated and even a trip to open space.",
        tryThis: { text: "Explore Nature", to: "/explore/nature" },
      },
    },
  },
  {
    id: "human-body",
    title: "The Human Body",
    emoji: "🫀",
    subject: "science",
    outcomes: [
      "uk-ks2-sc-2","cam-prim-sc-2","cc-sc-2","uae-moe-sc-1",
    ],
    tiers: {
      "5-7": {
        intro: "Your body is made of many parts working together — bones, muscles, heart, lungs and brain.",
        facts: [
          "You have 206 bones as an adult (babies have more — they fuse as you grow).",
          "Your heart beats about 100,000 times a day.",
          "Lungs bring oxygen in when you breathe.",
          "The brain sends messages to every part of you.",
          "Your five senses are sight, hearing, smell, taste and touch.",
        ],
        funFact: "Your bones are living — they can grow back if they break!",
        tryThis: { text: "Ask the Globe about the body", to: "/ask" },
      },
      "8-10": {
        intro: "Body systems each do a job: circulatory (blood), respiratory (breathing), digestive (food), nervous (signals), skeletal (support), muscular (movement).",
        facts: [
          "The heart pumps blood around a network of vessels (arteries, veins, capillaries).",
          "Lungs exchange oxygen for carbon dioxide with the blood.",
          "The digestive system breaks food into nutrients over about 24 hours.",
          "The brain and spinal cord form the central nervous system.",
          "Muscles work in pairs — one pulls, one relaxes (e.g. biceps and triceps).",
        ],
        funFact: "If stretched out, your blood vessels would wrap around Earth about 2.5 times!",
        tryThis: { text: "Ask the Globe about the body", to: "/ask" },
      },
      "11-15": {
        intro: "The human body is an integrated system of 11 organ systems, each maintaining homeostasis. Cells → tissues → organs → systems → organism.",
        facts: [
          "The circulatory system moves 5 litres of blood through 100,000 km of vessels.",
          "Neurons transmit signals at up to 120 m/s.",
          "The digestive tract absorbs nutrients across a 200 m² surface.",
          "DNA in every cell contains ~3 billion base pairs coding ~20,000 genes.",
          "Puberty is driven by hormones from the pituitary gland (FSH, LH, GH).",
        ],
        funFact: "You produce a new stomach lining every 3-4 days — otherwise stomach acid would digest you!",
        tryThis: { text: "Take the Knowledge Quiz", to: "/knowledge" },
      },
    },
  },
  {
    id: "materials",
    title: "Materials, Solids, Liquids & Gases",
    emoji: "🧪",
    subject: "science",
    outcomes: [
      "uk-ks1-sc-4","uk-ks2-sc-3","cam-prim-sc-3","cc-sc-3","uae-moe-sc-2",
    ],
    tiers: {
      "5-7": {
        intro: "Everything is made from something! Wood, glass, metal, plastic and water are all different materials.",
        facts: [
          "Solids keep their shape (a brick).",
          "Liquids flow and take the shape of the container (water).",
          "Gases spread out and fill any space (air).",
          "Metals are usually hard, shiny and cold to touch.",
          "Some materials float, others sink.",
        ],
        funFact: "Glass is made from sand! Very hot fires melt sand into a see-through liquid that cools into glass.",
        tryThis: { text: "Try coloring materials", to: "/coloring" },
      },
      "8-10": {
        intro: "Matter has three main states (solid, liquid, gas) that can change with temperature. Materials have properties that make them useful — hardness, flexibility, conductivity.",
        facts: [
          "Melting: solid → liquid. Freezing: liquid → solid.",
          "Evaporating: liquid → gas. Condensing: gas → liquid.",
          "Reversible change: melting ice. Irreversible: baking a cake.",
          "Insulators (plastic, wool) don't let heat pass through easily.",
          "Conductors (metals) do.",
        ],
        funFact: "Rubber comes from a tree! Sap from rubber trees was first used for waterproof coats in the 1700s.",
        tryThis: { text: "Ask the Globe about materials", to: "/ask" },
      },
      "11-15": {
        intro: "Matter comprises particles (atoms, molecules, ions) whose arrangement and motion determine state, properties and reactions.",
        facts: [
          "States of matter: solid (fixed lattice), liquid (close but mobile), gas (far apart, high energy), plasma (ionised gas).",
          "Chemical vs physical change: chemical creates a new substance (rust, combustion); physical does not (melting).",
          "The periodic table organises 118 elements by proton number.",
          "Bonds: ionic (transfer of electrons), covalent (sharing), metallic (delocalised sea of electrons).",
          "Density = mass/volume; determines whether an object floats.",
        ],
        funFact: "Diamond and graphite are both pure carbon — the only difference is how the atoms are arranged.",
        tryThis: { text: "Take a Science quiz", to: "/knowledge" },
      },
    },
  },
  {
    id: "space",
    title: "Space, Planets & Stars",
    emoji: "🚀",
    subject: "science",
    outcomes: [
      "uk-ks2-sc-4","cam-prim-sc-4","cc-sc-4","uae-moe-sc-3",
    ],
    tiers: {
      "5-7": {
        intro: "Space is above the sky. Earth is a planet spinning around a huge star we call the Sun. The Moon spins around us!",
        facts: [
          "There are 8 planets: Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune.",
          "The Sun is a star — it looks big because it's the closest one.",
          "Earth spins once every 24 hours — that gives us day and night.",
          "The Moon takes about a month to circle Earth.",
          "Astronauts wear special suits because there's no air in space.",
        ],
        funFact: "You would weigh 6 times less on the Moon than on Earth — you could jump like a superhero!",
        tryThis: { text: "Take the Space Cadets quiz", to: "/knowledge" },
      },
      "8-10": {
        intro: "Our Solar System has one star (the Sun), 8 planets, dozens of moons, and millions of rocks called asteroids and comets.",
        facts: [
          "Jupiter is the biggest planet — you could fit over 1000 Earths inside it.",
          "Saturn has spectacular rings made of ice and rock.",
          "Mars is called the Red Planet because of iron in its dust.",
          "A year on Earth = 365 days (one full orbit around the Sun).",
          "Light from the Sun takes 8 minutes to reach us.",
        ],
        funFact: "There's a giant storm on Jupiter — the 'Great Red Spot' — bigger than Earth and 350 years old!",
        tryThis: { text: "Explore Space Cadets", to: "/knowledge" },
      },
      "11-15": {
        intro: "The Solar System sits in one arm of the Milky Way galaxy, which is one of ~200 billion galaxies. Physics — gravity, nuclear fusion, orbital mechanics — governs everything.",
        facts: [
          "Kepler's laws describe planetary orbits (ellipses, equal areas, period² ∝ radius³).",
          "Stars fuse hydrogen into helium; when they run out, they die as red giants, white dwarfs, neutron stars or black holes.",
          "The Milky Way is ~100,000 light years across.",
          "The universe is ~13.8 billion years old and still expanding (Hubble's Law).",
          "Exoplanets (planets orbiting other stars) — thousands found since 1992.",
        ],
        funFact: "Neutron stars are so dense a teaspoon of one would weigh 6 billion tonnes on Earth.",
        tryThis: { text: "Space Cadets — hardest level", to: "/knowledge" },
      },
    },
  },

  // ═════════════════════════ ENGLISH ═════════════════════════
  {
    id: "phonics-reading",
    title: "Phonics & Reading",
    emoji: "🔤",
    subject: "english",
    outcomes: [
      "eyfs-lit-2","uk-ks1-en-1","uk-ks1-en-2","cam-prim-en-1","cc-ela-1",
    ],
    tiers: {
      "5-7": {
        intro: "Phonics is a way to read by sounding out letters. Each letter (or group of letters) makes a sound; put them together and you get a word!",
        facts: [
          "The alphabet has 26 letters — 5 vowels (a, e, i, o, u) and 21 consonants.",
          "'Ch' makes one sound as in chair.",
          "'Sh' makes one sound as in ship.",
          "Blend sounds together: c-a-t = cat.",
          "Sight words like 'the', 'is' and 'and' can't be sounded out — you just remember them.",
        ],
        funFact: "The English language has over 170,000 words — but you only need about 3000 to hold most conversations!",
        tryThis: { text: "Play Spell the Country", to: "/spell" },
      },
      "8-10": {
        intro: "Fluent reading combines decoding (sounding out) with comprehension (understanding meaning). Vocabulary, punctuation and rhythm all matter.",
        facts: [
          "Prefixes change meaning: un-happy, dis-like, re-do.",
          "Suffixes too: -ful, -less, -ing, -ed.",
          "A synonym is a word with the same meaning (big / large).",
          "An antonym is a word with the opposite (hot / cold).",
          "A homophone sounds the same but is spelled differently (there / their / they're).",
        ],
        funFact: "The longest word in the Oxford Dictionary is 'pneumonoultramicroscopicsilicovolcanoconiosis' — a lung disease.",
        tryThis: { text: "Try Spell the Country", to: "/spell" },
      },
      "11-15": {
        intro: "Advanced reading involves analysing tone, structure, rhetorical devices and authorial intent across fiction and non-fiction.",
        facts: [
          "Etymology traces word origins (e.g. 'geography' from Greek 'geo' = earth + 'graphein' = to write).",
          "Figurative language includes similes, metaphors, personification, hyperbole, alliteration.",
          "Text structures: chronological, cause-effect, compare-contrast, problem-solution.",
          "Register (formal / informal) is chosen based on audience and purpose.",
          "Critical reading questions bias, evidence, source reliability.",
        ],
        funFact: "Shakespeare invented over 1700 words we still use — including 'eyeball', 'lonely', 'bump' and 'gossip'.",
        tryThis: { text: "Ask the Globe a word question", to: "/ask" },
      },
    },
  },
  {
    id: "writing-grammar",
    title: "Writing & Grammar",
    emoji: "✏️",
    subject: "english",
    outcomes: [
      "eyfs-lit-3","uk-ks1-en-3","uk-ks2-en-2","cam-prim-en-2","cc-ela-2",
    ],
    tiers: {
      "5-7": {
        intro: "Writing lets you save your ideas so other people can read them. Every sentence starts with a capital letter and ends with a full stop (.).",
        facts: [
          "A sentence has a person/thing and something they do (Sam runs).",
          "Nouns name things (dog, chair, London).",
          "Verbs are doing words (run, jump, sing).",
          "Adjectives describe (big, red, happy).",
          "Use ! for excitement and ? for questions.",
        ],
        funFact: "The letter 'e' is the most used letter in English — more than 1 in 8 letters is 'e'!",
        tryThis: { text: "Play Spell the Country", to: "/spell" },
      },
      "8-10": {
        intro: "Good writing has clear ideas, correct grammar and rich vocabulary. Sentences vary in length; paragraphs group related ideas.",
        facts: [
          "Nouns, verbs, adjectives, adverbs, pronouns, prepositions, conjunctions, articles are the 8 word classes.",
          "Tenses: past (I walked), present (I walk), future (I will walk).",
          "A conjunction joins clauses: and, but, because, so.",
          "An apostrophe shows possession (Sam's book) or contraction (can't = cannot).",
          "Commas separate items in a list or clauses.",
        ],
        funFact: "The Oxford Comma (the last comma in 'red, white, and blue') can change legal meaning — a US dairy paid $5m in 2017 because a comma was missing.",
        tryThis: { text: "Ask the Globe to check a sentence", to: "/ask" },
      },
      "11-15": {
        intro: "Effective writing balances clarity, structure, tone and rhetorical impact. Grammar rules give a shared foundation; style choices give voice.",
        facts: [
          "Sentence types: simple, compound, complex, compound-complex.",
          "Active vs passive voice: 'The cat chased the mouse' vs 'The mouse was chased by the cat'.",
          "Modal verbs (can, might, should, must) express possibility or obligation.",
          "Cohesion is achieved via pronouns, connectives and lexical chains.",
          "Argumentative writing uses claim + evidence + reasoning.",
        ],
        funFact: "The first grammar of English was published in 1586 — before that, teachers just taught Latin grammar and hoped for the best.",
        tryThis: { text: "Ask the Globe for feedback", to: "/ask" },
      },
    },
  },

  // ═════════════════════════ HISTORY ═════════════════════════
  {
    id: "significant-people",
    title: "Explorers, Leaders & Changemakers",
    emoji: "🧭",
    subject: "history",
    outcomes: [
      "eyfs-utw-3","uk-ks1-hi-3","uk-ks2-hi-3","cam-prim-hi-1","cc-ss-7",
    ],
    tiers: {
      "5-7": {
        intro: "People from long ago changed the world by exploring, inventing and standing up for what's right.",
        facts: [
          "Neil Armstrong was the first person to walk on the Moon in 1969.",
          "Marie Curie discovered radium and won 2 Nobel Prizes.",
          "Nelson Mandela worked for equal rights in South Africa.",
          "Rosa Parks helped end segregation in the USA.",
          "Malala Yousafzai stood up for girls' education and won a Nobel Peace Prize.",
        ],
        funFact: "Ada Lovelace wrote the first ever computer program — 100 years before computers were built!",
        tryThis: { text: "History Quest quiz", to: "/knowledge" },
      },
      "8-10": {
        intro: "History is shaped by people — inventors, scientists, leaders and ordinary folk who did extraordinary things.",
        facts: [
          "Ibn Battuta travelled ~120,000 km across Africa, Asia and Europe in the 1300s.",
          "Zheng He led vast Chinese fleets across the Indian Ocean in the 1400s.",
          "Christopher Columbus reached the Americas in 1492 — though people had lived there for millennia.",
          "Mahatma Gandhi led India to independence through non-violent protest.",
          "Martin Luther King Jr led the civil rights movement in the USA.",
        ],
        funFact: "Ibn Battuta's travels covered 44 modern countries — more than any explorer before modern times.",
        tryThis: { text: "History Quest quiz", to: "/knowledge" },
      },
      "11-15": {
        intro: "Studying significant individuals reveals the interplay of agency, structure and context. Great leaders reflect and reshape the societies they inhabit.",
        facts: [
          "The Enlightenment (17th-18th c.) produced Newton, Voltaire, Locke — reshaping science and politics.",
          "Colonisation and decolonisation moved through leaders like Bolívar, Nehru, Nkrumah, Kenyatta.",
          "Scientific revolutions were led by Copernicus, Galileo, Darwin, Einstein.",
          "20th c. dictators (Hitler, Stalin, Mao) show how ideology and technology can amplify power catastrophically.",
          "Wangari Maathai (Kenya) won the Nobel Peace Prize for planting 30 million trees.",
        ],
        funFact: "Genghis Khan's DNA is estimated to be present in 1 in 200 men alive today — through 13th c. Mongol expansion.",
        tryThis: { text: "History Quest — hardest level", to: "/knowledge" },
      },
    },
  },
  {
    id: "ancient-civilisations",
    title: "Ancient Civilisations",
    emoji: "🏛️",
    subject: "history",
    outcomes: [
      "uk-ks2-hi-1","uk-ks3-hi-1","cam-prim-hi-2","cc-ss-8",
    ],
    tiers: {
      "5-7": {
        intro: "Thousands of years ago, people lived in huge cities and built amazing things. We call these ancient civilisations.",
        facts: [
          "Ancient Egyptians built pyramids as tombs for their kings called pharaohs.",
          "Ancient Greeks invented the Olympic Games and democracy.",
          "The Romans built roads, baths and roads across Europe.",
          "The Mayans lived in Central America and made a very accurate calendar.",
          "Ancient Chinese built the Great Wall — so long you can walk on it for months!",
        ],
        funFact: "The Great Wall of China is over 21,000 km long — longer than the distance from London to Sydney.",
        tryThis: { text: "History Quest", to: "/knowledge" },
      },
      "8-10": {
        intro: "Ancient civilisations grew where farming worked — beside great rivers. They invented writing, laws, maths and religion.",
        facts: [
          "Mesopotamia (Iraq): cradle of writing (cuneiform, c.3200 BC).",
          "Egypt: hieroglyphs, pyramids, mummification along the Nile.",
          "Indus Valley (Pakistan/India): planned cities with drainage 4500 years ago.",
          "Ancient China: silk, paper, gunpowder, compass — all invented here.",
          "Ancient Greece: philosophy, geometry, drama, the Olympics.",
        ],
        funFact: "The Rosetta Stone (196 BC) let us finally read Egyptian hieroglyphs — it has the same message in 3 scripts.",
        tryThis: { text: "History Quest quiz", to: "/knowledge" },
      },
      "11-15": {
        intro: "Early civilisations arose independently in Mesopotamia, Egypt, the Indus Valley, China, Mesoamerica and the Andes. Each developed writing, cities, law and specialised labour.",
        facts: [
          "Code of Hammurabi (~1750 BC) — one of the earliest written legal codes.",
          "The Silk Road connected Han China to the Roman Empire from ~130 BC.",
          "The Mayan Long Count calendar tracked 5125-year cycles.",
          "Roman engineering: aqueducts, concrete, roads — many still in use.",
          "Fall of civilisations often combined climate stress, political instability and invasion.",
        ],
        funFact: "The Antikythera Mechanism (c.150 BC) was an ancient Greek analogue computer that predicted eclipses and planetary positions.",
        tryThis: { text: "History Quest — hardest level", to: "/knowledge" },
      },
    },
  },

  // ═════════════════════════ LANGUAGES ═════════════════════════
  {
    id: "greetings-languages",
    title: "Hello Around the World",
    emoji: "👋",
    subject: "languages",
    outcomes: [
      "uk-ks2-la-1","cam-prim-la-1","ib-pyp-lang-1","uae-moe-lang-1","cc-ela-3",
    ],
    tiers: {
      "5-7": {
        intro: "There are over 7000 languages in the world. Saying 'hello' is a great way to make a friend.",
        facts: [
          "Hola — Spanish (Spain, Mexico, most of South America).",
          "Bonjour — French (France, Canada, parts of Africa).",
          "Nǐ hǎo — Mandarin Chinese.",
          "Konnichiwa — Japanese.",
          "Marhaba — Arabic.",
        ],
        funFact: "Papua New Guinea has 840 languages — more than any other country!",
        tryThis: { text: "Learn World Words", to: "/languages" },
      },
      "8-10": {
        intro: "Languages are grouped into families. Related languages share ancestors — like siblings and cousins.",
        facts: [
          "Indo-European family: English, Spanish, Hindi, Russian, Persian.",
          "Sino-Tibetan family: Mandarin, Cantonese, Tibetan.",
          "Afro-Asiatic: Arabic, Hebrew, Amharic.",
          "Bantu: Swahili, Zulu, Xhosa.",
          "Mandarin has the most speakers (~1.1 billion); English is spoken in most countries.",
        ],
        funFact: "Icelandic has barely changed in 1000 years — a modern Icelander can read Viking sagas.",
        tryThis: { text: "Learn a Language", to: "/languages" },
      },
      "11-15": {
        intro: "Linguistics studies how languages work — phonology (sounds), morphology (word formation), syntax (grammar), semantics (meaning), pragmatics (context).",
        facts: [
          "Language families: Indo-European, Sino-Tibetan, Niger-Congo, Afro-Asiatic, Austronesian, Dravidian, Uralic, Turkic.",
          "Endangered languages: nearly half of the world's languages are at risk of extinction this century.",
          "Sapir-Whorf hypothesis: language shapes thought (debated).",
          "Sign languages are fully-formed languages, not translations of spoken ones.",
          "Esperanto is a constructed language designed in 1887 to promote peace.",
        ],
        funFact: "The Xhosa language uses 15 different click sounds — try saying 'Xhosa' with a click at the start!",
        tryThis: { text: "Learn a Language", to: "/languages" },
      },
    },
  },

  // ═════════════════════════ ART & MUSIC ═════════════════════════
  {
    id: "art-design",
    title: "Drawing, Painting & Design",
    emoji: "🎨",
    subject: "art",
    outcomes: [
      "eyfs-ea-1","uk-ks1-art-1","uk-ks1-art-2","uk-ks2-art-1","uk-ks2-art-2",
      "uk-ks3-art-1","cam-prim-ar-1","ib-pyp-ar-1","ib-myp-ar-1","uae-moe-art-1",
      "cc-art-1",
    ],
    tiers: {
      "5-7": {
        intro: "Art is how we show what we see, feel and imagine. You can use paint, pencils, clay, paper, sticks — anything!",
        facts: [
          "Primary colors: red, yellow, blue. Mix any two and you get a new colour.",
          "Warm colours (red, orange, yellow) feel bright and happy.",
          "Cool colours (blue, green, purple) feel calm.",
          "Sculptors shape clay, wood or stone into 3-D art.",
          "Every drawing starts with a line — try wavy, straight and zig-zag.",
        ],
        funFact: "The oldest known cave paintings are 45,500 years old — a wild pig painted in Indonesia!",
        tryThis: { text: "Colour a Landmark", to: "/coloring" },
      },
      "8-10": {
        intro: "Artists plan their work using elements like line, shape, colour, texture and space. Practising sketches builds skill.",
        facts: [
          "The colour wheel arranges hues so you can find complementary pairs (opposite: red-green, blue-orange).",
          "Symmetry is when two sides mirror each other; asymmetry can feel more dynamic.",
          "Famous artists: Leonardo da Vinci, Frida Kahlo, Vincent van Gogh, Yayoi Kusama, Kehinde Wiley.",
          "Design uses art to solve problems — logos, posters, apps, buildings.",
          "Sketchbooks are for practice — mistakes are welcome!",
        ],
        funFact: "Van Gogh painted 900 paintings in only 10 years — one every 4 days!",
        tryThis: { text: "Try Pixel Art coloring", to: "/coloring" },
      },
      "11-15": {
        intro: "Art history traces movements from cave painting to contemporary digital media. Composition, tone, perspective and cultural context all shape meaning.",
        facts: [
          "Renaissance (14-17th c.): perspective, anatomy, humanism (Michelangelo, Leonardo).",
          "Impressionism (1870s): light and colour over line (Monet, Degas, Cassatt).",
          "Cubism (1907): multiple viewpoints on one canvas (Picasso, Braque).",
          "Islamic art: geometric patterns and calligraphy (no figurative in religious spaces).",
          "Contemporary art spans installation, performance, digital, AI-generated media.",
        ],
        funFact: "Banksy shredded his own painting seconds after it sold at auction for £1m in 2018 — the shredded version later sold for £18m.",
        tryThis: { text: "Try Landmark coloring", to: "/coloring" },
      },
    },
  },
  {
    id: "music",
    title: "Music: Rhythm, Song & Notation",
    emoji: "🎵",
    subject: "art",
    outcomes: [
      "eyfs-ea-2","uk-ks1-mu-1","uk-ks1-mu-2","uk-ks2-mu-1","uk-ks2-mu-2",
    ],
    tiers: {
      "5-7": {
        intro: "Music is sound arranged in a pattern. You can make it by clapping, singing, tapping or playing an instrument.",
        facts: [
          "Rhythm is the beat you can clap along to.",
          "Melody is the tune you can hum.",
          "Loud sounds are called forte; soft sounds piano.",
          "Fast music is allegro; slow is largo.",
          "Percussion (drums), strings (guitar, violin), wind (flute), brass (trumpet) — four instrument families.",
        ],
        funFact: "Elephants can play music together — zoos have taught elephants to play xylophones in time!",
        tryThis: { text: "Explore Cultures", to: "/explore/cultures" },
      },
      "8-10": {
        intro: "Music is written using notes on a staff of 5 lines. Notes tell you the pitch (how high/low) and how long to play them.",
        facts: [
          "The 7 note names: A, B, C, D, E, F, G — then they repeat higher.",
          "Whole note = 4 beats, half note = 2, quarter note = 1.",
          "Time signature 4/4 means 4 beats per bar (most pop songs).",
          "Major keys often sound happy; minor keys sound sad.",
          "An orchestra usually has 4 sections: strings, woodwinds, brass, percussion.",
        ],
        funFact: "The world's longest concert has been playing since 2001 in Halberstadt, Germany — it won't end until 2640!",
        tryThis: { text: "Explore Cultures", to: "/explore/cultures" },
      },
      "11-15": {
        intro: "Western music theory codifies pitch, rhythm, harmony and form. Every culture has its own systems — Indian ragas, Chinese pentatonics, African polyrhythms.",
        facts: [
          "12 semitones per octave; major/minor scales pick 7 of them in patterns.",
          "Chords stack notes (root + 3rd + 5th = triad).",
          "Common forms: verse-chorus, sonata, rondo, 12-bar blues.",
          "Digital audio samples sound waves 44,100 times per second (CD quality).",
          "Global genres: jazz (USA), reggae (Jamaica), K-pop (Korea), Bhangra (Punjab), Afrobeats (Nigeria).",
        ],
        funFact: "The oldest known musical instrument is a 40,000-year-old flute made from a vulture bone.",
        tryThis: { text: "Explore Cultures", to: "/explore/cultures" },
      },
    },
  },

  // ═════════════════════════ COMPUTING / ICT ═════════════════════════
  {
    id: "computing-code",
    title: "Coding, Algorithms & E-Safety",
    emoji: "💻",
    subject: "ict",
    outcomes: [
      "uk-ks1-cp-1","uk-ks1-cp-2","uk-ks2-cp-1","uk-ks2-cp-2","uk-ks2-cp-3",
      "uk-ks2-cp-4","uk-ks3-cp-1","uk-ks3-cp-2","uk-ks3-cp-3","cam-prim-ict-1",
      "cam-lsec-ict-1","uae-moe-ict-1","cc-tech-1","ib-myp-de-1",
    ],
    tiers: {
      "5-7": {
        intro: "A computer follows a list of steps called an algorithm. Coding means telling the computer what to do, one step at a time.",
        facts: [
          "A robot brushing its teeth would need steps: pick up brush, add paste, brush 30 seconds, rinse.",
          "Computers can't guess — every step must be clear.",
          "Bugs are mistakes in code (named after a real moth found in a computer in 1947!).",
          "Never share your name, home or password online — that's e-safety.",
          "If something online scares you, tell a grown-up.",
        ],
        funFact: "The first computer game was made in 1958 — Tennis for Two — on a machine bigger than a car!",
        tryThis: { text: "Ask the Globe", to: "/ask" },
      },
      "8-10": {
        intro: "Programs use sequence (one step after another), selection (if/then choices), and repetition (loops) to solve problems.",
        facts: [
          "Popular kids' languages: Scratch (block-based), Python, JavaScript.",
          "A loop runs a block many times: 'repeat 4 times: turn right'.",
          "An 'if' statement picks a path: 'if score > 10, say Well done!'.",
          "The internet connects billions of computers via cables and satellites.",
          "Strong passwords are long, unique, and never shared.",
        ],
        funFact: "There are over 700 programming languages in use today — but 5 of them cover 80% of jobs.",
        tryThis: { text: "Take a Knowledge Quiz", to: "/knowledge" },
      },
      "11-15": {
        intro: "Computer science is the study of computation, information, and automation. It sits under maths but touches every discipline.",
        facts: [
          "Binary: 0s and 1s represent all digital data (letters, images, sound).",
          "Boolean logic: AND, OR, NOT — the basis of every circuit.",
          "Common algorithms: sorting, searching, shortest path, encryption.",
          "Hardware layers: transistor → logic gate → chip → device.",
          "AI models like ChatGPT are trained on billions of text examples using neural networks.",
        ],
        funFact: "Ada Lovelace wrote the first computer algorithm in 1843 — 100 years before the first electronic computer was built.",
        tryThis: { text: "Code & Computers quiz", to: "/knowledge" },
      },
    },
  },

  // ═════════════════════════ PE ═════════════════════════
  {
    id: "physical-education",
    title: "PE, Health & Fitness",
    emoji: "⚽",
    subject: "pe",
    outcomes: [
      "eyfs-pd-1","uk-ks1-pe-1","uk-ks2-pe-1","uk-ks2-pe-2","ib-myp-pe-1",
      "uae-moe-pe-1","cc-pe-1",
    ],
    tiers: {
      "5-7": {
        intro: "Moving your body keeps you strong, happy and healthy. Running, jumping, throwing and catching are the building blocks of every sport.",
        facts: [
          "Kids should aim for at least 60 minutes of active play every day.",
          "Warming up first (stretch, jog) helps prevent injuries.",
          "Water helps your body work — drink lots during exercise.",
          "Balance activities include hopping, tightrope walking, standing on one leg.",
          "Team games teach fairness, taking turns and cheering others on.",
        ],
        funFact: "A cheetah can run 100 metres in about 6 seconds — faster than any human ever!",
        tryThis: { text: "Play Squishy Hop", to: "/squishy" },
      },
      "8-10": {
        intro: "Skills like running, jumping and catching combine into games with rules — football, netball, basketball, swimming.",
        facts: [
          "Attacking = getting the ball to the goal. Defending = stopping the other team.",
          "The Olympics started in ancient Greece around 776 BC.",
          "Swimming safely means never swimming alone and knowing how to float on your back.",
          "Team sports teach communication and strategy — not just fitness.",
          "Rest and sleep are how muscles grow after exercise.",
        ],
        funFact: "The Olympic torch is lit from the sun using a curved mirror at Olympia, Greece — the same way for 100 years!",
        tryThis: { text: "Squishy Hop math game", to: "/squishy" },
      },
      "11-15": {
        intro: "Physical education combines fitness, skill acquisition, tactics and understanding of health. Elite athletes train aerobic, anaerobic and neuromuscular systems.",
        facts: [
          "Cardiovascular fitness measured by VO₂ max (millilitres of O₂ per kg per min).",
          "Muscle types: type I (endurance, red), type II (power, white).",
          "Balanced diet: carbs (energy), protein (repair), fats (long-term energy), vitamins, water.",
          "BMI, waist-to-hip ratio and resting heart rate are common fitness indicators.",
          "Overtraining without rest causes fatigue and injury — recovery matters.",
        ],
        funFact: "Usain Bolt's 100 m record (9.58 s) means an average speed of 37.6 km/h — faster than most city speed limits!",
        tryThis: { text: "Take on Squishy Hop", to: "/squishy" },
      },
    },
  },

  // ═════════════════════════ RE / WORLD RELIGIONS ═════════════════════════
  {
    id: "world-religions",
    title: "World Religions & Beliefs",
    emoji: "🕊️",
    subject: "re",
    outcomes: [
      "uk-ks1-re-1","uk-ks2-re-1","uae-moe-isl-1",
    ],
    tiers: {
      "5-7": {
        intro: "People around the world believe different things about who we are and how to live well. These are called religions or beliefs.",
        facts: [
          "Christians follow the teachings of Jesus and celebrate Christmas and Easter.",
          "Muslims follow Islam, pray 5 times a day and celebrate Eid.",
          "Hindus celebrate Diwali (festival of lights) and Holi (colours).",
          "Jews celebrate Hanukkah with candles and Passover with a special meal.",
          "Buddhists follow the Buddha's teachings on kindness and calm.",
        ],
        funFact: "Diwali is celebrated with millions of tiny oil lamps — some cities look like starry skies from above!",
        tryThis: { text: "Explore Cultures", to: "/explore/cultures" },
      },
      "8-10": {
        intro: "The 5 major world religions each have sacred texts, festivals and places of worship. Billions of people practise them today.",
        facts: [
          "Christianity: Bible, church, ~2.4 billion followers, largest religion worldwide.",
          "Islam: Quran, mosque, ~1.9 billion, dominant in the Middle East, N. Africa, S.E. Asia.",
          "Hinduism: Vedas, temple (mandir), ~1.2 billion, mainly in India.",
          "Buddhism: Tripitaka, temple/pagoda, ~520 million, across Asia.",
          "Judaism: Torah, synagogue, ~15 million, world's oldest monotheistic religion.",
        ],
        funFact: "Mecca in Saudi Arabia hosts the Hajj — the largest annual human gathering in the world, with over 2 million pilgrims.",
        tryThis: { text: "Explore Cultures Hub", to: "/explore/cultures" },
      },
      "11-15": {
        intro: "Religious studies examines the beliefs, ethics, rituals and philosophies that have shaped civilisations. Understanding difference builds respect.",
        facts: [
          "Monotheism (one god): Christianity, Islam, Judaism, Sikhism.",
          "Polytheism (many gods): historic Greek, Norse, Egyptian; contemporary Hinduism (aspects of one).",
          "Non-theistic paths: Buddhism, Jainism, Taoism, Confucianism.",
          "Secularism separates religion from government (constitutional in France, USA, India).",
          "Interfaith dialogue works to build understanding across traditions.",
        ],
        funFact: "The Golden Rule ('treat others as you'd want to be treated') appears in almost every major world religion, in some form.",
        tryThis: { text: "Explore Cultures", to: "/explore/cultures" },
      },
    },
  },

  // ═════════════════════════ PSHE / WELLBEING ═════════════════════════
  {
    id: "wellbeing-relationships",
    title: "Health, Feelings & Friendships",
    emoji: "💛",
    subject: "pshe",
    outcomes: [
      "uk-ks2-ps-1",
    ],
    tiers: {
      "5-7": {
        intro: "Looking after your body, your feelings and your friends helps you feel happy. Every big feeling is okay — telling someone helps.",
        facts: [
          "Big feelings: happy, sad, angry, worried, excited. All are normal.",
          "Deep breaths calm your body when feelings feel too big.",
          "Eating fruit, veg and water gives you energy.",
          "Sleep helps your brain and body grow — 9-11 hours a night for kids.",
          "Kind words make friendships strong.",
        ],
        funFact: "Laughing 15 minutes burns as many calories as 5 minutes on a bike!",
        tryThis: { text: "Ask the Globe", to: "/ask" },
      },
      "8-10": {
        intro: "PSHE (Personal, Social, Health, Economic) teaches how to keep yourself safe and healthy, and how to build good relationships.",
        facts: [
          "Mental health matters as much as physical — everyone has ups and downs.",
          "Being a good friend means listening, being honest, and standing up for what's fair.",
          "Bullying is never okay — always tell a trusted adult.",
          "Consent means asking before you touch, share, or use someone's stuff.",
          "Screen time is best in balance with movement, sleep and outside play.",
        ],
        funFact: "Talking to a trusted adult when things feel hard is a superpower — it makes almost any worry smaller.",
        tryThis: { text: "Ask the Globe", to: "/ask" },
      },
      "11-15": {
        intro: "Adolescence brings physical change, more independence, and complex relationships. PSHE at this stage covers mental health, consent, digital citizenship, money, and civic responsibility.",
        facts: [
          "Puberty timing varies — anywhere from 8-14 for most people. Nothing is 'wrong' with early or late.",
          "Consent must be freely given, informed, enthusiastic and can be withdrawn anytime.",
          "Digital footprint: anything posted online can be screenshot and shared forever.",
          "Budgeting basics: needs vs wants, saving, avoiding impulse spending.",
          "Mental health first aid: notice, ask, listen, help find help.",
        ],
        funFact: "Studies show teens who spend 1-2 hours outdoors a day have better sleep, focus and mood — even during school-heavy weeks.",
        tryThis: { text: "Ask the Globe about wellbeing", to: "/ask" },
      },
    },
  },

  // ═════════════════════════ GLOBAL PERSPECTIVES ═════════════════════════
  {
    id: "global-citizenship",
    title: "Global Citizenship & Sustainability",
    emoji: "🌱",
    subject: "geography",
    outcomes: [
      "cam-prim-gp-1","ib-pyp-so-3","ib-myp-so-1","cc-ss-9","uae-moe-ss-4",
    ],
    tiers: {
      "5-7": {
        intro: "Everyone on Earth shares one planet. Being a good global citizen means caring for it — and for other people too.",
        facts: [
          "Recycle: paper, plastic, metal, glass all can become new things.",
          "Turn off lights and taps when you don't need them.",
          "Bees and trees help clean our air.",
          "The UN (United Nations) has 193 countries working together for peace.",
          "Every language, food and dance in the world makes it more beautiful.",
        ],
        funFact: "One tree can absorb the CO₂ that a car makes in 26,000 km of driving.",
        tryThis: { text: "Explore Nature", to: "/explore/nature" },
      },
      "8-10": {
        intro: "Global issues — climate change, poverty, plastic waste — need everyone to help. Small choices add up when billions of people make them.",
        facts: [
          "The UN Sustainable Development Goals (SDGs) are 17 shared targets for a better world by 2030.",
          "Fair trade means farmers in developing countries get paid fairly.",
          "The 3 Rs: Reduce, Reuse, Recycle — in that order of importance.",
          "Renewable energy: solar, wind, hydro. Non-renewable: coal, oil, gas.",
          "Empathy is trying to understand how others feel — the base of global citizenship.",
        ],
        funFact: "If every UK household hung 1 load of laundry outside instead of tumble-drying it, we'd save enough energy to power 300,000 homes for a year.",
        tryThis: { text: "Explore Wonders", to: "/explore/wonders" },
      },
      "11-15": {
        intro: "Global citizenship extends rights and responsibilities beyond national borders. Sustainability integrates environment, society and economy.",
        facts: [
          "UN SDGs: 17 goals, 169 targets, 232 indicators covering poverty, hunger, health, education, gender, climate…",
          "Carbon footprint: total CO₂ equivalent of your actions; individual cuts + systemic change both matter.",
          "Cultural competence: understanding different worldviews, communication styles and values.",
          "Democracy, human rights, rule of law are pillars of open societies.",
          "Migration reshapes cities — 1 in 30 humans now lives outside their country of birth.",
        ],
        funFact: "The Paris Agreement (2015) is the first legally-binding climate treaty with 195 signatories — the whole world minus a few small holdouts.",
        tryThis: { text: "Explore Wonders", to: "/explore/wonders" },
      },
    },
  },

  // ═════════════════════════ SCIENCE (extra depth) ═════════════════════════
  {
    id: "forces-energy",
    title: "Forces, Electricity & Sound",
    emoji: "⚡",
    subject: "science",
    outcomes: [
      "uk-ks2-sc-5","uk-ks2-sc-6","uk-ks2-sc-7","ib-pyp-sc-3","uk-ks3-sc-3",
    ],
    tiers: {
      "5-7": {
        intro: "A force is a push or a pull. Electricity powers lights and toys. Sound is what your ears hear when things wobble.",
        facts: [
          "Gravity pulls everything down towards Earth — that's why balls fall.",
          "A magnet pulls metal things like iron and steel.",
          "A battery stores electricity for torches and toys.",
          "Loud sounds have big wobbles; quiet sounds have small ones.",
          "Sound needs something to travel through — usually air.",
        ],
        funFact: "You can't hear sound in space — there's no air for it to travel through, so astronauts use radios.",
        tryThis: { text: "Ask the Globe about forces", to: "/ask" },
      },
      "8-10": {
        intro: "Forces make things start, stop, speed up, slow down or change direction. Electricity flows in circuits. Sound travels as waves.",
        facts: [
          "Gravity, friction, air resistance and water resistance all slow things down.",
          "Simple circuit = battery + wires + bulb + switch.",
          "A magnet has 2 poles: north and south. Same poles push apart; opposite poles pull together.",
          "Sound travels ~340 m/s in air. Light is a million times faster.",
          "Materials are conductors (metals) or insulators (plastic, rubber).",
        ],
        funFact: "Bats and dolphins use echolocation — they shout, then listen to the echoes to 'see' with sound.",
        tryThis: { text: "Take a Science quiz", to: "/knowledge" },
      },
      "11-15": {
        intro: "Physics describes matter, energy, forces and waves using mathematical laws. Newton's laws, Ohm's law, wave equations and conservation of energy underpin every technology.",
        facts: [
          "Newton's 3 laws: inertia, F=ma, action-reaction.",
          "Electric current: I = V / R (Ohm's law). Power: P = V × I.",
          "Wave properties: wavelength, frequency, amplitude. v = f × λ.",
          "Energy is conserved — cannot be created or destroyed, only transferred (heat, light, kinetic, potential).",
          "Electromagnetic spectrum: radio → microwave → IR → visible → UV → X-ray → gamma.",
        ],
        funFact: "The largest hydroelectric dam (Three Gorges, China) is so massive that when full, its water shifted Earth's rotation by 0.06 microseconds.",
        tryThis: { text: "Take a Science quiz", to: "/knowledge" },
      },
    },
  },
  {
    id: "scientific-method",
    title: "Working Scientifically",
    emoji: "🔍",
    subject: "science",
    outcomes: [
      "uk-ks3-sc-4","cam-lsec-sc-1","ib-myp-sc-1",
    ],
    tiers: {
      "5-7": {
        intro: "Scientists ask questions and try to find out. They look, wonder, test and share what they learn.",
        facts: [
          "Watch carefully — that's called observing.",
          "Ask a question, like 'do plants grow better in the sun?'",
          "Try it! Put one plant in the sun and one in the dark.",
          "Look at what happens over a week.",
          "Tell someone what you found.",
        ],
        funFact: "Scientists once thought maggots grew from meat — a curious kid's experiment with jars proved that flies laid eggs first!",
        tryThis: { text: "Ask the Globe a question", to: "/ask" },
      },
      "8-10": {
        intro: "The scientific method is a way to find things out that anyone can check. It uses a hypothesis, an experiment, results and a conclusion.",
        facts: [
          "Hypothesis: a smart guess you can test.",
          "Fair test: change only ONE thing, keep everything else the same.",
          "Record results in a table or chart.",
          "Conclusion: did your results match your hypothesis?",
          "Peer review means other scientists check your work.",
        ],
        funFact: "One of the most-repeated experiments ever is dropping objects from towers — Galileo proved a heavy ball and a light ball fall at the same speed in 1590.",
        tryThis: { text: "Ask the Globe", to: "/ask" },
      },
      "11-15": {
        intro: "Scientific practice combines hypothesis generation, controlled experimentation, statistical analysis and peer-reviewed communication. Replicability is the gold standard.",
        facts: [
          "Independent variable = what you change. Dependent = what you measure. Controlled = what you keep constant.",
          "Sample size and statistical significance (p-values) determine confidence.",
          "Correlation ≠ causation — a link is not proof.",
          "Peer review + replication + preregistration reduce bias.",
          "Modern breakthroughs (mRNA vaccines, CRISPR, LIGO) all followed this pipeline.",
        ],
        funFact: "The CERN experiment that confirmed the Higgs boson in 2012 involved 6000 physicists analysing collisions from 40 million events per second.",
        tryThis: { text: "Take a Science quiz", to: "/knowledge" },
      },
    },
  },

  // ═════════════════════════ HISTORY (extra depth) ═════════════════════════
  {
    id: "british-history",
    title: "British History: Tudors to Today",
    emoji: "🇬🇧",
    subject: "history",
    outcomes: [
      "uk-ks2-hi-7","uk-ks3-hi-2","uk-ks3-hi-3","uk-ks3-hi-4",
    ],
    tiers: {
      "5-7": {
        intro: "Long ago in Britain, there were kings, queens, castles and knights. Britain has been changed by many groups of people over 1000 years.",
        facts: [
          "The Romans arrived in Britain about 2000 years ago.",
          "The Vikings sailed from Scandinavia to raid and settle.",
          "William the Conqueror won the Battle of Hastings in 1066.",
          "Queen Victoria ruled for 63 years in the 1800s.",
          "Queen Elizabeth II ruled for 70 years — longer than anyone else.",
        ],
        funFact: "The Tower of London is over 900 years old and used to keep lions, polar bears and even an elephant!",
        tryThis: { text: "History Quest quiz", to: "/knowledge" },
      },
      "8-10": {
        intro: "British history is often taught in three chunks after 1066: Tudors (1485-1603), Stuarts & Georgians (1603-1837), Victorians & Modern (1837-today).",
        facts: [
          "Henry VIII had 6 wives and started the Church of England in 1534.",
          "Elizabeth I (his daughter) ruled during Shakespeare's time.",
          "The Industrial Revolution (1760-1840) invented factories, trains and steam power.",
          "The British Empire once covered a quarter of the world's land.",
          "WWI (1914-18) and WWII (1939-45) reshaped Europe and the world.",
        ],
        funFact: "The London Underground opened in 1863 — 40 years before New York's — and used steam trains at first (imagine the smoke!).",
        tryThis: { text: "History Quest quiz", to: "/knowledge" },
      },
      "11-15": {
        intro: "Britain's modern history (1509 onwards) reshaped politics, religion, industry and global affairs. Understanding it is key to understanding today's UK, Commonwealth and world.",
        facts: [
          "Reformation (1534): Henry VIII broke with Rome — the beginnings of Protestantism in England.",
          "English Civil War (1642-51): parliament vs monarchy; execution of Charles I.",
          "Industrial Revolution: steam, mass production, urbanisation, empire.",
          "Abolition of slavery in the British Empire: 1833 (23 years after the trade was banned).",
          "20th century: Two World Wars, welfare state, decolonisation, EU membership then Brexit.",
        ],
        funFact: "The NHS (National Health Service) was founded in 1948 — one of the world's first universal healthcare systems, and free at the point of use ever since.",
        tryThis: { text: "History Quest — hardest level", to: "/knowledge" },
      },
    },
  },

  // ═════════════════════════ MATHS (extra depth) ═════════════════════════
  {
    id: "algebra-basics",
    title: "Algebra: Letters that Stand for Numbers",
    emoji: "🧮",
    subject: "maths",
    outcomes: [
      "uk-ks2-ma-7","uk-ks3-ma-1","cam-lsec-ma-1","ib-myp-ma-1",
    ],
    tiers: {
      "5-7": {
        intro: "Sometimes we don't know a number yet, so we use a shape or a letter instead. It's like a puzzle to figure out.",
        facts: [
          "If ☐ + 3 = 7, then ☐ must be 4.",
          "If 2 × ☐ = 10, then ☐ must be 5.",
          "Letters do the same job: n + 3 = 7 means n = 4.",
          "Both sides of an '=' balance like a see-saw.",
          "You can guess and check to solve simple ones.",
        ],
        funFact: "Word 'algebra' comes from an Arabic book, 'Al-jabr' by Al-Khwarizmi (~820 AD) — his name gave us the word 'algorithm' too.",
        tryThis: { text: "Try Maths Academy", to: "/maths-academy" },
      },
      "8-10": {
        intro: "In algebra, letters stand for unknown numbers. You use rules of addition, subtraction, multiplication and division to solve for them.",
        facts: [
          "Variables: x, y, n, a, b — any letter can be a number.",
          "Expression: 3x + 5 (no equals). Equation: 3x + 5 = 20 (has equals).",
          "Solve 3x + 5 = 20: subtract 5, get 3x = 15. Divide by 3, x = 5.",
          "Missing number problems: what × 4 = 24? Solve n × 4 = 24, so n = 6.",
          "Sequences follow patterns: 2, 5, 8, 11… add 3 each time (nth term = 3n − 1).",
        ],
        funFact: "The word 'variable' is from Latin meaning 'that which can change' — perfect for a letter that stands for any number.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
      "11-15": {
        intro: "Algebra generalises arithmetic — its rules apply to all real (and later, complex) numbers. It's the foundation of every quantitative discipline.",
        facts: [
          "Expanding brackets: a(b + c) = ab + ac (distributive).",
          "Factorising: x² + 5x + 6 = (x + 2)(x + 3).",
          "Quadratic formula: x = (−b ± √(b² − 4ac)) / 2a.",
          "Sequences: arithmetic (add), geometric (multiply). nth term formulas.",
          "Simultaneous equations solved by substitution or elimination.",
        ],
        funFact: "Al-Khwarizmi's 820 AD book is where we get both 'algebra' AND 'algorithm' — from the Latin spelling of his name.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "statistics-data",
    title: "Data, Charts & Probability",
    emoji: "📊",
    subject: "maths",
    outcomes: [
      "uk-ks2-ma-10","uk-ks3-ma-4","ib-pyp-ma-3",
    ],
    tiers: {
      "5-7": {
        intro: "You can turn what you count into a picture. That's called a chart. It makes big numbers easy to see.",
        facts: [
          "Tally chart: draw one stick for each thing you count, cross the 5th diagonally.",
          "Pictogram: use pictures — each picture stands for a number.",
          "Bar chart: taller bars mean bigger numbers.",
          "Ask: what's the most? What's the least?",
          "Data means information you can count or measure.",
        ],
        funFact: "The first bar chart is over 200 years old — invented by Scottish engineer William Playfair in 1786.",
        tryThis: { text: "Take a Knowledge quiz", to: "/knowledge" },
      },
      "8-10": {
        intro: "Statistics is a way to make sense of lots of data. Line graphs show change; bar charts compare groups; pie charts show fractions of a whole.",
        facts: [
          "Mean (average): add up all values, divide by how many.",
          "Mode: the most-common value. Median: the middle value.",
          "Range: biggest minus smallest.",
          "Probability: how likely something is, from 0 (impossible) to 1 (certain).",
          "50/50 chance is 1/2 — like flipping a fair coin.",
        ],
        funFact: "A UK-based Netflix analyst found the perfect binge speed by studying viewer data: 3 episodes per sitting = optimum enjoyment!",
        tryThis: { text: "Try Maths Academy", to: "/maths-academy" },
      },
      "11-15": {
        intro: "Statistics summarises data (descriptive) and infers conclusions from samples (inferential). Probability underpins predictions from weather to insurance to AI.",
        facts: [
          "Mean, median, mode, range, IQR, standard deviation are the 6 core summary stats.",
          "P(A ∩ B) = P(A) × P(B) for independent events; P(A ∪ B) = P(A) + P(B) − P(A ∩ B).",
          "Correlation coefficient (r) ranges from −1 (perfect negative) to +1 (perfect positive).",
          "Sampling: random, stratified, systematic — choice affects bias.",
          "Machine learning is essentially applied statistics over huge datasets.",
        ],
        funFact: "The lottery odds of winning the UK EuroMillions jackpot are 1 in 139,838,160 — you're 300 times more likely to be struck by lightning this year.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
  {
    id: "patterns-ratio",
    title: "Patterns, Ratio & Proportion",
    emoji: "🔗",
    subject: "maths",
    outcomes: [
      "ib-pyp-ma-5","uk-ks3-ma-2",
    ],
    tiers: {
      "5-7": {
        intro: "A pattern is something that repeats. In maths, patterns follow a rule — like +2, +2, +2 makes 2, 4, 6, 8.",
        facts: [
          "Shape patterns: 🔴🔵🔴🔵🔴🔵 — what comes next?",
          "Number patterns: 5, 10, 15, 20 (add 5).",
          "Growing patterns: 1, 3, 6, 10, 15… (adds 2, then 3, then 4).",
          "Ratio is like sharing: for every 1 apple you get, I get 2. That's ratio 1:2.",
          "Everyday ratios: 1 cup of rice needs 2 cups of water.",
        ],
        funFact: "The Fibonacci sequence (1, 1, 2, 3, 5, 8, 13…) shows up in sunflowers, pinecones and even hurricane shapes.",
        tryThis: { text: "Try a puzzle", to: "/puzzles" },
      },
      "8-10": {
        intro: "Ratios compare quantities. Proportions describe how something changes as another quantity changes. Both are used every day — in cooking, maps and money.",
        facts: [
          "Ratio 2:3 means 2 parts to 3 parts (e.g. mix 2 cups sugar to 3 cups flour).",
          "Scale on a map: 1:100,000 means 1 cm on paper = 100,000 cm (1 km) in real life.",
          "Direct proportion: as one goes up, the other goes up (double the recipe = double the ingredients).",
          "Inverse proportion: as one goes up, the other goes down (more workers = less time).",
          "Percentage as ratio: 25% = 25:100 = 1:4.",
        ],
        funFact: "The 'golden ratio' (~1.618) appears in the Parthenon, the Mona Lisa and even nautilus shells — artists have used it for 2500 years.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
      "11-15": {
        intro: "Ratio, proportion and rates of change form the algebra of relationships. Direct and inverse variation, best-buy comparisons, exchange rates and unit rates all live here.",
        facts: [
          "Direct variation: y = kx (k = constant).",
          "Inverse variation: y = k/x.",
          "Compound measures: speed (km/h), density (kg/m³), pressure (N/m²).",
          "Percentage change: (new − old) / old × 100.",
          "Compound interest: A = P(1 + r/n)^(nt).",
        ],
        funFact: "Currency arbitrage relies on tiny ratio differences — banks use it billions of times a second to profit from fractions of a penny.",
        tryThis: { text: "Take Maths Academy", to: "/maths-academy" },
      },
    },
  },
];

// ═════════════════════════ CROSS-CURRICULUM EXTRA COVERAGE ═════════════════════════
// Many syllabus outcomes across UK/IB/Cambridge/UAE/Common Core teach the
// SAME theme under different codes (e.g. "Ancient Egypt" appears in UK KS2
// history, IB PYP social, UAE history…). Rather than duplicate the same
// hand-authored content per curriculum, we bulk-append additional
// outcome codes to existing TOPICS entries below. This keeps
// `/learn-topics` teaching cards visible for every matching outcome
// across every supported curriculum.
const EXTRA_OUTCOMES = {
  "continents-oceans":        ["ib-pyp-so-2"],
  "world-countries-capitals": ["uk-ks1-geo-4"],
  "climate-weather":          ["uk-ks3-geo-5", "uk-ks2-geo-6", "ib-pyp-sc-2"],
  "counting-place-value":     ["ib-pyp-ma-1"],
  "shapes-geometry":          ["uk-ks2-ma-9", "ib-pyp-ma-2", "uk-ks3-ma-3"],
  "measurement-time-money":   ["uk-ks2-ma-8", "ib-pyp-ma-4"],
  "living-things":            ["uk-ks2-sc-10", "uk-ks3-sc-1", "ib-pyp-sc-1"],
  "human-body":               ["uk-ks2-sc-10"],
  "materials":                ["uk-ks2-sc-8", "uk-ks2-sc-9", "uk-ks3-sc-2", "ib-pyp-sc-4"],
  "space":                    ["uk-ks3-sc-3", "ib-pyp-sc-2"],
  "phonics-reading":          ["eyfs-lit-1", "uk-ks1-en-4", "uk-ks2-en-1", "uk-ks2-en-6", "uk-ks3-en-1", "cam-prim-en-3", "cam-lsec-en-1", "ib-pyp-lang-2", "ib-pyp-lang-3", "ib-myp-lang-1", "uae-moe-en-1", "uae-moe-ar-1", "uae-moe-ar-2", "cc-ela-4"],
  "writing-grammar":          ["uk-ks2-en-3", "uk-ks2-en-4", "uk-ks2-en-5", "uk-ks3-en-2", "uk-ks3-en-3", "cc-ela-5"],
  "significant-people":       ["uk-ks1-hi-1", "uk-ks1-hi-2", "uk-ks3-hi-4", "ib-pyp-so-2"],
  "ancient-civilisations":    ["uk-ks2-hi-2", "uk-ks2-hi-4", "uk-ks2-hi-5", "uk-ks2-hi-6"],
  "greetings-languages":      ["uk-ks2-la-2", "uk-ks2-la-3", "uk-ks3-la-1", "ib-myp-lang-2"],
  "art-design":               ["uae-moe-ar2-1", "cc-ar-1"],
  "music":                    ["cc-ar-2"],
  "wellbeing-relationships":  ["uk-ks1-ps-1", "ib-pyp-ps-1"],
  "physical-education":       ["ib-pyp-ps-1"],
  "world-religions":          ["uae-moe-is-1"],
  "global-citizenship":       ["ib-pyp-so-1", "cam-lsec-gp-1"],
};

for (const t of TOPICS) {
  const extra = EXTRA_OUTCOMES[t.id];
  if (extra && extra.length) {
    // De-duplicate — some codes may already be in the topic's outcomes.
    const set = new Set([...t.outcomes, ...extra]);
    t.outcomes = [...set];
  }
}

/**
 * Reverse index: outcome code → matching topics.
 * Built once at module load so `LearnTopics.jsx` can look up teaching
 * content for any syllabus outcome in O(1).
 */
export const TOPIC_BY_OUTCOME = (() => {
  const map = {};
  for (const topic of TOPICS) {
    for (const code of topic.outcomes) {
      (map[code] = map[code] || []).push(topic);
    }
  }
  return map;
})();

// Dev-only invariant: warn if any keyObjective outcomeCode is missing from
// the topic index. Keeps the highlighted objectives from silently linking
// to a not-yet-authored topic (regression guard for iter 22's dead-link
// bug on uk-ks3-geo-5). Lives here (not in keyObjectives.js) to avoid
// circular imports; TOPIC_BY_OUTCOME is the source of truth.
if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
  // Deferred require so tree-shakers don't drag keyObjectives.js into prod.
  import("./keyObjectives.js").then((mod) => {
    if (typeof mod.keyObjectivesForAge !== "function") return;
    const tiers = [null, 6, 9, 13];
    for (const age of tiers) {
      for (const obj of mod.keyObjectivesForAge(age)) {
        if (!TOPIC_BY_OUTCOME[obj.outcomeCode]) {
          console.warn(
            "[topicContent] keyObjectives references outcome",
            obj.outcomeCode,
            "which has no matching teaching topic. Add it to TOPICS[].outcomes or EXTRA_OUTCOMES.",
          );
        }
      }
    }
  }).catch(() => { /* ignore in envs where dynamic import unsupported */ });
}

/**
 * Pick the best age tier for a given age.
 * @param {number|null} age
 * @returns {"5-7"|"8-10"|"11-15"}
 */
export function tierForAge(age) {
  if (age == null) return "8-10";
  if (age <= 7) return "5-7";
  if (age <= 10) return "8-10";
  return "11-15";
}

/** Convenience — get the first topic and its age-tuned tier for an outcome. */
export function teachingForOutcome(code, age) {
  const topics = TOPIC_BY_OUTCOME[code];
  if (!topics || topics.length === 0) return null;
  const topic = topics[0];
  const tier = topic.tiers[tierForAge(age)];
  return { topic, tier };
}
